/* NarratorCheatSheet — overlay that lists all your active shortcuts.
 *
 * Toggled by pressing `?` (Shift+/) on a non-editable element,
 * OR by the configurable "open-cheat-sheet" action (default Ctrl+Alt+/).
 */
import React, { useEffect, useState } from 'react';
import { Keyboard, X, Sparkles } from 'lucide-react';
import {
  NARRATOR_ACTIONS, loadShortcuts, formatBinding,
} from '../utils/narratorShortcuts';

function isEditableTarget(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  return false;
}

export default function NarratorCheatSheet() {
  const [open, setOpen] = useState(false);
  const [bindings, setBindings] = useState(() => loadShortcuts());

  // Listen for `?` (Shift+/) AND custom event from GlobalNarrator action
  useEffect(() => {
    const reload = () => setBindings(loadShortcuts());
    const openEvt = () => setOpen(true);
    const onKey = (e) => {
      // `?` when not typing — quick peek
      if (e.key === '?' && !isEditableTarget(e.target)) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      // Esc to close
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('narrator-shortcuts-changed', reload);
    window.addEventListener('open-narrator-cheatsheet', openEvt);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('narrator-shortcuts-changed', reload);
      window.removeEventListener('open-narrator-cheatsheet', openEvt);
    };
  }, [open]);

  if (!open) return null;

  // Group: bound vs unbound
  const bound = NARRATOR_ACTIONS.filter((a) => bindings[a.id] && bindings[a.id].key);

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
      data-testid="narrator-cheatsheet"
    >
      <div
        className="bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50 rounded-3xl max-w-lg w-full shadow-2xl border-2 border-amber-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3.5 border-b border-amber-200 flex items-center justify-between bg-gradient-to-r from-amber-100 to-pink-100 rounded-t-3xl">
          <div>
            <h2 className="text-base font-bold text-amber-800 flex items-center gap-2">
              <Keyboard className="w-4 h-4" /> Dina kortkommandon
            </h2>
            <p className="text-[11px] text-amber-700/70 italic">
              Tryck <code className="px-1 bg-white rounded font-mono">?</code> för att visa/dölja
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="narrator-cheatsheet-close"
            className="p-2 rounded-full hover:bg-white/60 text-amber-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-1 max-h-[70vh] overflow-y-auto">
          {bound.length === 0 && (
            <p className="text-sm text-amber-700/70 italic text-center py-6">
              Inga genvägar inställda än. Öppna Berättare-panelen för att lägga till 💛
            </p>
          )}
          {bound.map((a) => {
            const b = bindings[a.id];
            return (
              <div
                key={a.id}
                className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/70 hover:bg-white border border-amber-100"
                data-testid={`cheatsheet-row-${a.id}`}
              >
                <span className="text-xl">{a.emoji}</span>
                <span className="flex-1 text-sm text-gray-700">{a.label}</span>
                <kbd className="px-2.5 py-1 rounded-md bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 text-[11px] font-bold font-mono text-gray-700 shadow-sm">
                  {formatBinding(b)}
                </kbd>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-2.5 border-t border-amber-200 bg-amber-50/60 text-[10px] text-amber-700/80 text-center rounded-b-3xl flex items-center justify-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Esc stänger · Klick utanför stänger också · Ändra i Berättare-panelen
        </div>
      </div>
    </div>
  );
}
